import {
    Cadence,
    getSubscriptionSignatureData,
    Network,
    SubscriptionType,
    USDC_POLYGON,
} from '@spritz-finance/sdk'
import { Body, Spacer } from '@spritz-finance/ui'
import { useEffect } from 'react'
import {
    SMART_PAY_MESSAGE_REJECTED,
    SMART_PAY_MESSAGE_SIGNED,
} from '../../../../../../analytics/events'
import { Row } from '../../../../../../components/Layout/Row'
import { Spinner } from '../../../../../../components/Spinner/Spinner'
import { useAnalytics } from '../../../../../../context/useAnalytics'
import { useWeb3Connection } from '../../../../../../web3/Web3Provider'
import { isProduction } from '../../../../../app/config'
import { fiatAmountToTokenInputMax, formatPaymentReference, SignatureParams } from '../signature'
import { SubscriptionData } from '../SubscriptionForm'
import { useCreateSubscription } from '../useCreateSubscription'
import { getDateFromDay } from '../utils'
import { SubscriptionAccount } from './CreateSubscriptionButton'

export const CreateSubscription = ({
    account,
    data,
    toNextStep,
}: {
    account: SubscriptionAccount
    data: SubscriptionData
    toNextStep: () => void
}) => {
    const { trackAnalyticsEvent } = useAnalytics()
    const staging = !isProduction()
    const { createSubscription } = useCreateSubscription()
    const { account: address, signer } = useWeb3Connection()
    const firstPayment = getDateFromDay(data.day)
    const paymentAmount = Number(data.amount)

    const handleCreateSubscription = async (
        signedMessage: string,
        signatureParams: SignatureParams
    ) => {
        await createSubscription({
            accountId: account.id,
            provider: account.provider,
            startDate: firstPayment.toDate(),
            totalPayments: Number(data.totalPayments) - 1,
            amount: Number(data.amount),
            walletAddress: address!,
            signedMessage,
            signatureParams,
        })
        toNextStep()
    }

    const signSubscriptionMessage = async () => {
        const paymentAmountMax = fiatAmountToTokenInputMax(paymentAmount, USDC_POLYGON.decimals)

        const params: SignatureParams = {
            startTime: Math.floor(firstPayment.toDate().getTime() / 1000),
            paymentToken: USDC_POLYGON.address.toLowerCase(),
            paymentAmountMax,
            cadence: Cadence.Monthly,
            subscriptionType: SubscriptionType.DIRECT,
            totalPayments: Number(data.totalPayments) - 1,
            paymentReference: formatPaymentReference(account.id),
        }

        const message = getSubscriptionSignatureData(Network.Polygon, params, staging)

        try {
            const signedMessage = await signer?._signTypedData(...message)
            if (signedMessage) {
                trackAnalyticsEvent(SMART_PAY_MESSAGE_SIGNED)
                await handleCreateSubscription(signedMessage, params)
            }
        } catch (e) {
            // @ts-ignore
            if (e.code === 'ACTION_REJECTED') {
                trackAnalyticsEvent(SMART_PAY_MESSAGE_REJECTED)
            }
        }
    }

    useEffect(() => {
        signSubscriptionMessage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            <Spinner />
            <Row align="center">
                <Body.Reg size={16} align="center">
                    Please sign the transaction in your connected wallet...
                </Body.Reg>
            </Row>
            <Spacer height={48} />
        </>
    )
}
